import { LitElement, css, html, nothing, svg } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import { IconSizes } from '../../shared/types/icons.model';
/**
 * Icon component
 */
@customElement('luds-icon')
export class LudsIcon extends LitElement {
  static override styles = [
    overrideDesignTokens,
    css`
      :host {
        --luds-icon-default-fill-color: #5f6871;
        --luds-icon-size-extra-extra-small: var(--icon-size-xxs);
        --luds-icon-size-extra-small: var(--icon-size-xs);
        --luds-icon-size-small: var(--icon-size-s);
        --luds-icon-size-medium: var(--icon-size-md);
        --luds-icon-size-large: var(--icon-size-l);

        display: inline-block;
        fill: var(--luds-icon-size-small);
        width: var(--luds-icon-size-small);
        height: var(--luds-icon-size-small);
      }

      :host([size='extra-extra-small']) {
        width: var(--luds-icon-size-extra-extra-small);
        height: var(--luds-icon-size-extra-extra-small);
      }

      :host([size='extra-small']) {
        width: var(--luds-icon-size-extra-small);
        height: var(--luds-icon-size-extra-small);
      }

      :host([size='small']) {
        width: var(--luds-icon-size-small);
        height: var(--luds-icon-size-small);
      }

      :host([size='medium']) {
        width: var(--luds-icon-size-medium);
        height: var(--luds-icon-size-medium);
      }

      :host([size='large']) {
        width: var(--luds-icon-size-large);
        height: var(--luds-icon-size-large);
      }

      /** 12px **/
      .extra-extra-small {
        width: var(--luds-icon-size-extra-extra-small);
        height: var(--luds-icon-size-extra-extra-small);
      }

      /** 16px **/
      .extra-small {
        width: var(--luds-icon-size-extra-small);
        height: var(--luds-icon-size-extra-small);
      }

      /** 20px **/
      .small {
        width: var(--luds-icon-size-small);
        height: var(--luds-icon-size-small);
      }

      /** 24px **/
      .medium {
        width: var(--luds-icon-size-medium);
        height: var(--luds-icon-size-medium);
      }

      /** 36px **/
      .large {
        width: var(--luds-icon-size-large);
        height: var(--luds-icon-size-large);
      }
    `,
  ];

  /** Specifies the icon to be rendered */
  @property({ type: String })
  icon: string;

  /**  Specifies the icon size, valid values are 'small', 'medium' and 'large'. If no value is provided the size will default to 'medium' */
  @property({ type: String, reflect: true })
  size: IconSizes = 'medium';

  /** Specifies the icon color, value is passed to the host <svg> fill property */
  @property({ type: String })
  color: string;

  /** Specifies the icon title, value is passed to the host <svg> title property */
  @property({ type: String })
  override title: string;

  /** Specifies the icon description, value is passed to the host <svg> desc property */
  @property({ type: String })
  description: string;

  @state()
  private svg: string;

  override connectedCallback() {
    super.connectedCallback();
    this._importSvgIcon();
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('icon')) {
      this._importSvgIcon();
    }
  }

  private async _importSvgIcon(): Promise<void> {
    if (!this.icon) return;

    try {
      const icon = await import(`./iconography/${this.icon}.ts`);
      this.svg = icon[`${this.icon}`];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error loading icon: ${this.icon}`, error);
      this.svg = '';

      this.dispatchEvent(new CustomEvent('luds-icon-loading-error', { detail: error }));
    }
  }

  override render() {
    return this.icon && this.svg
      ? svg`<svg role="img" class="${this.size}" fill="${this.color}" viewBox="0 0 18 18">
          ${this.title ? svg`<title>${this.title}</title>` : nothing}
          ${this.description ? svg`<desc>${this.description}</desc>` : nothing}
            
          ${unsafeSVG(this.svg)}
        </svg>`
      : html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-icon': LudsIcon;
  }
}
